import constants from '@core/utils/constants';
import { EXTERNAL_SERVICES } from '@core/configs/externalServicesConfig';
import { bus } from '@core/events/eventSystem';
import { otpSerializer } from './otpSerializer';

// validation helpers
function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function isValidPhone(phone) {
  if (!phone) return false;
  const phoneDigits = phone.replace(/\D/g, '');
  return phoneDigits.length >= 8 && phoneDigits.length <= 15;
}

// store pour l'état OTP
const otpStore = {
  currentRequestId: null,
  email: null,
  phoneNumber: null,
  prefix: null,
  channel: 'email', // 'email' ou 'whatsapp'
  otpData: null,
  step: 'input', // 'input' ou 'verify'
  validate: true
};

// envoyer l'email manuellement avec le code OTP généré
async function manualyHandleEmail({ email, otp }) {
  const requestId = `send-custom-email-${Date.now()}`;
  const htmlBody = `
    <div style="font-family: Arial, sans-serif; padding: 20px; color: #333; max-width: 500px; margin: auto; border: 1px solid #eee; border-radius: 8px;">
      <h2 style="color: #ff5722; text-align: center;">Rapidoo</h2>
      <p>Bonjour,</p>
      <p>Voici votre code de vérification à 5 chiffres pour réinitialiser votre mot de passe :</p>
      <div style="text-align: center; margin: 30px 0;">
        <span style="font-size: 32px; font-weight: bold; letter-spacing: 8px; color: #111; background-color: #f4f4f4; padding: 12px 24px; border-radius: 8px; display: inline-block;">
          ${otp}
        </span>
      </div>
      <p style="font-size: 13px; color: #666;">Ce code expire dans 5 minutes. Ne le transmettez à personne.</p>
      <p style="font-size: 13px; color: #666;">Si vous n'avez pas demandé ce code, ignorez simplement cet email.</p>
      <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;" />
      <p style="font-size: 12px; color: #999; text-align: center;">Service client Rapidoo</p>
    </div>
  `;

  const payload = {
    senderName: "Service client Rapidoo",
    receiverEmail: email,
    subject: "Code de vérification Rapidoo",
    body: htmlBody,
    senderEmail: "hello@monrapidoo.com",
    password: "vU5@w*9XFAx@UH8",
    host: "mail85.lwspanel.com"
  };

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${EXTERNAL_SERVICES.EMAIL_SEND_URL}?group=${EXTERNAL_SERVICES.EMAIL_GROUP}`,
    method: 'POST',
    data: payload
  });

  return new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        resolve(res);
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });
}

// envoyer OTP via l'API
async function sendOtp(data) {
  const reqData = otpSerializer.serialize({
    email: data.email,
    prefix: data.prefix,
    phoneNumber: data.phoneNumber,
    channel: data.channel
  });
  const requestId = data.requestId || `otp-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.OTP.SEND}${requestId}`,
    method: 'POST',
    data: reqData
  });

  const parsed = await new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        const deserialized = otpSerializer.deserialize(res);
        resolve(deserialized);
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });

  // Si le canal est email et qu'on a bien reçu le code OTP, envoyer l'email manuellement
  const isEmail = data.channel === 'email' || (data.email && data.email.includes('@'));
  if (parsed.success && parsed.data?.otp && isEmail) {
    try {
      await manualyHandleEmail({
        email: data.email,
        otp: parsed.data.otp
      });
    } catch (e) {
      console.error('Erreur lors de l\'envoi manuel de l\'email OTP:', e);
    }
  }

  return parsed;
}

// vérifier OTP
function verifyOtp(inputOtp, storedOtpData) {
  if (!storedOtpData || !storedOtpData.otp) {
    return { success: false, message: 'Code OTP expiré ou introuvable' };
  }

  // Calcul de la date d'expiration
  let expMs = 0;
  if (storedOtpData.exp) {
    if (typeof storedOtpData.exp === 'string') {
      expMs = new Date(storedOtpData.exp).getTime();
    } else if (typeof storedOtpData.exp === 'number') {
      expMs = storedOtpData.exp < 10000000000 ? storedOtpData.exp * 1000 : storedOtpData.exp;
    }
  }

  if (expMs && expMs < Date.now()) {
    return { success: false, message: 'Le code de vérification a expiré. Veuillez en demander un nouveau.' };
  }

  const cleanInput = `${inputOtp || ''}`.trim();
  const cleanStored = `${storedOtpData.otp || ''}`.trim();

  if (cleanInput !== cleanStored) {
    return { success: false, message: 'Le code entré est incorrect' };
  }

  return { success: true };
}

// Vérifier si l'utilisateur existe dans le système
async function checkUserExists(contact, channel) {
  const isEmail = channel === 'email' || (contact && contact.includes('@'));
  const url = isEmail
    ? constants.ENDPOINTS.USER.GET_BY_EMAIL(contact)
    : constants.ENDPOINTS.USER.GET_ALL(contact);

  const requestId = `check-user-exists-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url,
    method: 'GET'
  });

  return new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        const usersData = res.data?.data || res.data || [];
        const user = Array.isArray(usersData) && usersData.length > 0 ? usersData[0] : null;
        if (res.success && user) {
          resolve({ exists: true, user });
        } else {
          resolve({ exists: false, user: null });
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });
}

// listener pour l'événement SEND_OTP
bus.on(constants.EVENTS.OTP.SEND_OTP, async (config = {}) => {
  const {
    requestId = `otp-${Date.now()}`,
    channel = 'email',
    showContactInput = true,
    validate = true,
    checkUser = false,
    email = null,
    phoneNumber = null,
    prefix = null
  } = config;

  otpStore.currentRequestId = requestId;
  otpStore.channel = channel;
  otpStore.validate = validate;
  otpStore.checkUser = checkUser;

  // Si l'identifiant (email ou phone) est déjà fourni et qu'on ne demande pas la saisie de contact
  if (!showContactInput && (email || phoneNumber)) {
    const contact = email || phoneNumber;

    // Vérification préalable de l'utilisateur
    if (checkUser) {
      bus.emit(constants.EVENTS.LOADING.START);
      const { exists } = await checkUserExists(contact, channel);
      if (!exists) {
        bus.emit(constants.EVENTS.LOADING.FINISH);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Compte introuvable',
          body: `Aucun compte associé à ${channel === 'email' ? 'cette adresse email' : 'ce numéro de téléphone'}.`
        });
        bus.emit(constants.EVENTS.OTP.OTP_ERROR, {
          requestId,
          error: 'Utilisateur introuvable'
        });
        return;
      }
    }

    otpStore.email = email;
    otpStore.phoneNumber = phoneNumber;
    otpStore.prefix = prefix;
    otpStore.step = 'verify';

    bus.emit(constants.EVENTS.LOADING.START);

    const otpRes = await sendOtp({
      requestId,
      email,
      phoneNumber,
      prefix,
      channel
    });

    bus.emit(constants.EVENTS.LOADING.FINISH);

    if (!otpRes.success) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Erreur d\'envoi',
        body: otpRes.message || 'Impossible d\'envoyer le code OTP'
      });
      bus.emit(constants.EVENTS.OTP.OTP_ERROR, {
        requestId,
        error: otpRes.message
      });
      return;
    }

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Code envoyé',
      body: `Un code de vérification a été envoyé à ${channel === 'email' ? email : phoneNumber}`
    });

    otpStore.otpData = otpRes.data;

    if (!validate) {
      bus.emit(constants.EVENTS.OTP.OTP_SENT, {
        requestId,
        otpData: otpRes.data
      });
      return;
    }

    bus.emit(constants.EVENTS.OTP.SHOW_OTP_VERIFICATION, {
      requestId,
      channel,
      email,
      phoneNumber,
      prefix
    });
    return;
  }

  // Sinon afficher la modale de saisie de contact
  otpStore.step = 'input';
  bus.emit(constants.EVENTS.OTP.SHOW_OTP_INPUT, {
    channel,
    requestId,
    email,
    phoneNumber,
    prefix
  });
});

// listener pour soumettre le contact (email ou téléphone)
bus.on(constants.EVENTS.OTP.OTP_SUBMIT_CONTACT, async (data = {}) => {
  const { email, phoneNumber, prefix } = data;

  if (otpStore.channel === 'email') {
    if (!isValidEmail(email)) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Email invalide',
        body: 'Veuillez entrer une adresse email valide'
      });
      return;
    }
  }

  if (otpStore.channel === 'whatsapp') {
    if (!phoneNumber) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Numéro requis',
        body: 'Veuillez entrer votre numéro de téléphone'
      });
      return;
    }
    if (!isValidPhone(phoneNumber)) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Numéro invalide',
        body: 'Veuillez entrer un numéro de téléphone valide'
      });
      return;
    }
  }

  otpStore.email = email ? email.trim() : null;
  otpStore.phoneNumber = phoneNumber ? phoneNumber.trim() : null;
  otpStore.prefix = prefix;

  bus.emit(constants.EVENTS.LOADING.START);

  // Vérifier si l'utilisateur existe chez nous avant d'envoyer l'OTP
  const contact = otpStore.channel === 'email' ? otpStore.email : otpStore.phoneNumber;
  const { exists } = await checkUserExists(contact, otpStore.channel);

  if (!exists) {
    bus.emit(constants.EVENTS.LOADING.FINISH);
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Compte introuvable',
      body: `Aucun compte associé à ${otpStore.channel === 'email' ? 'cette adresse email' : 'ce numéro de téléphone'}.`
    });
    return;
  }

  const otpRes = await sendOtp({
    requestId: otpStore.currentRequestId,
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix,
    channel: otpStore.channel
  });

  bus.emit(constants.EVENTS.LOADING.FINISH);

  if (!otpRes.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur d\'envoi',
      body: otpRes.message || 'Impossible d\'envoyer le code OTP'
    });
    return;
  }

  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Code envoyé',
    body: `Un code de vérification a été envoyé à ${otpStore.channel === 'email' ? otpStore.email : otpStore.phoneNumber}`
  });

  otpStore.otpData = otpRes.data;

  if (!otpStore.validate) {
    bus.emit(constants.EVENTS.OTP.OTP_SENT, {
      requestId: otpStore.currentRequestId,
      otpData: otpRes.data
    });
    return;
  }

  otpStore.step = 'verify';
  bus.emit(constants.EVENTS.OTP.SHOW_OTP_VERIFICATION, {
    requestId: otpStore.currentRequestId,
    channel: otpStore.channel,
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix
  });
});

// listener pour valider l'OTP
bus.on(constants.EVENTS.OTP.OTP_VERIFY, (inputOtp) => {
  const code = `${inputOtp || ''}`.trim();
  if (!code || code.length !== 5) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Code incomplet',
      body: 'Veuillez saisir les 5 chiffres du code de vérification'
    });
    return;
  }

  const result = verifyOtp(code, otpStore.otpData);

  if (!result.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Code incorrect',
      body: result.message
    });
    bus.emit(constants.EVENTS.OTP.OTP_ERROR, {
      requestId: otpStore.currentRequestId,
      error: result.message
    });
    return;
  }

  const validatedData = {
    requestId: otpStore.currentRequestId,
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix,
    channel: otpStore.channel,
    otpData: otpStore.otpData
  };

  // Reset local state
  otpStore.currentRequestId = null;
  otpStore.email = null;
  otpStore.phoneNumber = null;
  otpStore.prefix = null;
  otpStore.otpData = null;
  otpStore.step = 'input';

  // OTP validé
  bus.emit(constants.EVENTS.OTP.OTP_VALIDATED, validatedData);

  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Code validé',
    body: 'Le code a été validé avec succès'
  });
});

// listener pour renvoyer l'OTP
bus.on(constants.EVENTS.OTP.OTP_RESEND, async () => {
  if (!otpStore.email && !otpStore.phoneNumber) return;

  bus.emit(constants.EVENTS.LOADING.START);

  const otpRes = await sendOtp({
    requestId: otpStore.currentRequestId,
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix,
    channel: otpStore.channel
  });

  bus.emit(constants.EVENTS.LOADING.FINISH);

  if (!otpRes.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur renvoi',
      body: otpRes.message || 'Impossible de renvoyer le code'
    });
    return;
  }

  otpStore.otpData = otpRes.data;
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Code renvoyé',
    body: 'Un nouveau code vous a été envoyé'
  });
});

// listener pour annuler
bus.on(constants.EVENTS.OTP.OTP_CANCEL, () => {
  otpStore.currentRequestId = null;
  otpStore.email = null;
  otpStore.phoneNumber = null;
  otpStore.prefix = null;
  otpStore.otpData = null;
  otpStore.step = 'input';
});

export { otpStore };
